/**
 * FILA DO PRÓXIMO LOTE — formulário de captura que aparece quando o lote atual esgota.
 *
 * É o antigo formulário da lista de espera (mesma rota `POST /api/public/waitlist`, mesma tag no
 * CRM), agora sem as datas da pré-venda de setembro, que já passaram.
 *
 * TRÊS CAMPOS: nome, e-mail e WhatsApp. O telefone derruba conversão em formulário frio, mas o dono
 * decidiu manter (2026-09-05): o WhatsApp é canal de aviso da abertura, não enfeite de cadastro.
 */
import { useCallback, useState } from "react";
import { ArrowUpRight, Check, Loader2 } from "lucide-react";
import { joinWaitlist, captureAttribution } from "@/lib/api";

/** Nos builds de preview interno o formulário não tem backend acessível (o CORS da API pública só
 *  libera os domínios do site). Fingir o sucesso deixa o revisor ver a confirmação. */
const PREVIEW = Boolean(import.meta.env.VITE_PREVIEW_PAGE);

const MIN_PHONE_DIGITS = 12; // DDI (2) + DDD (2) + número (8+)

/** Formata enquanto digita: +55 11 999999999. */
function formatPhone(v: string) {
  const d = v.replace(/\D/g, "").slice(0, 13);
  if (!d) return "+";
  let out = "+" + d.slice(0, 2);
  if (d.length > 2) out += " " + d.slice(2, 4);
  if (d.length > 4) out += " " + d.slice(4);
  return out;
}

function Confirmacao({ proximoLote }: { proximoLote: string }) {
  const [copiado, setCopiado] = useState(false);
  const linkIndicacao = `${window.location.origin}/?utm_source=indicacao&utm_medium=fila`;

  return (
    <div className="hts-confirm">
      <div className="hts-confirm-badge">
        <Check size={18} /> Você está na fila.
      </div>
      <p className="hts-confirm-lead">
        Quando o <strong>{proximoLote}</strong> abrir, você recebe o aviso por e-mail e WhatsApp
        antes da abertura geral.
      </p>
      <p className="hts-confirm-sub">Deixa o e-mail favoritado para o aviso não cair no spam.</p>

      <div className="hts-confirm-block">
        <div className="hts-confirm-block-title">Conhece alguém?</div>
        <p>Alguém que paga quatro assinaturas pra fazer o que a HTS faz numa? Manda esse link.</p>
        <button
          type="button"
          className="hts-confirm-copy"
          data-cursor-hover
          onClick={() => {
            navigator.clipboard?.writeText(linkIndicacao).then(
              () => { setCopiado(true); setTimeout(() => setCopiado(false), 2400); },
              () => {},
            );
          }}
        >
          {copiado ? "Link copiado" : "Copiar link"}
        </button>
      </div>
    </div>
  );
}

export function WaitlistForm({ proximoLote }: { proximoLote: string }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("+55");
  const [website, setWebsite] = useState(""); // honeypot
  const [status, setStatus] = useState<"idle" | "loading" | "done" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!email || !name) return;
      if (phone.replace(/\D/g, "").length < MIN_PHONE_DIGITS) {
        setStatus("error");
        setErrorMsg("WhatsApp incompleto — inclui código do país e DDD, ex: +55 11 999999999.");
        return;
      }
      setStatus("loading");
      if (PREVIEW) {
        setTimeout(() => setStatus("done"), 600);
        return;
      }
      const result = await joinWaitlist({
        email,
        firstName: name,
        phone,
        website,
        consentText: `Quero entrar na fila do ${proximoLote} da HTS e receber o aviso de abertura por e-mail e WhatsApp.`,
        ...captureAttribution(),
      });
      if (result.ok) {
        setStatus("done");
      } else {
        setStatus("error");
        setErrorMsg(result.error || "Algo deu errado — tenta de novo.");
      }
    },
    [email, name, phone, website, proximoLote]
  );

  if (status === "done") return <Confirmacao proximoLote={proximoLote} />;

  return (
    <form className="hts-waitlist-form" onSubmit={handleSubmit}>
      {/* Honeypot. O `name` NÃO pode ser "website"/"url"/"company": autofill e gerenciador de senha
          preenchem esses nomes mesmo fora da tela e disparam o filtro anti-spam pra gente real
          (achado em produção 2026-09-05). */}
      <input
        type="text"
        value={website}
        onChange={(e) => setWebsite(e.target.value)}
        name="hts_confirmar_2x"
        tabIndex={-1}
        autoComplete="off"
        data-lpignore="true"
        data-1p-ignore="true"
        className="hts-honeypot"
        aria-hidden="true"
      />
      <input type="text" required placeholder="Seu nome" value={name} onChange={(e) => setName(e.target.value)} className="hts-waitlist-input" />
      <input type="email" required placeholder="seu@email.com" value={email} onChange={(e) => setEmail(e.target.value)} className="hts-waitlist-input" />
      <input
        type="tel"
        required
        placeholder="WhatsApp — ex: +55 11 999999999"
        value={phone}
        onChange={(e) => setPhone(formatPhone(e.target.value))}
        className="hts-waitlist-input"
        style={{ flex: "1 1 100%" }}
      />
      <button type="submit" className="lv2-cta-btn" disabled={status === "loading"} data-cursor-hover>
        {status === "loading" ? <Loader2 size={16} className="hts-spin" /> : <>Entrar na fila do {proximoLote}<ArrowUpRight size={18} /></>}
      </button>
      {status === "error" && <div className="hts-waitlist-error">{errorMsg}</div>}
    </form>
  );
}
